<script setup lang="ts">
import { ref, onMounted } from "vue";
import { useRoute } from "vue-router";
import { envVars } from "../../envVars.ts";
import AnimatedLoadingDots from "../components/AnimatedLoadingDots.vue";

// Reactive state variables
const isLoading = ref(true);
const isValidated = ref(false);
const hasError = ref(false);
const errorMessage =
  "Missing or Incorrect Credentials, this has been reported and your details will be removed manually if they still exist in our system.";

// Extract URL parameters
const route = useRoute();
const uid = route.query.uid as string;
const csrfToken = route.query.csrf as string;

// Direct link to Google Form
const googleFormUrl = envVars.googleFormUrl;

onMounted(async () => {
  // Check if uid and csrfToken exist
  if (!uid || !csrfToken) {
    // Log Error 'Missing required parameters: user ID or verification token'
    hasError.value = true;
    isLoading.value = false;
    return;
  }

  // Basic client-side validation
  if (!/^[a-zA-Z0-9_-]{1,128}$/.test(uid)) {
    // Log Error Invalid user ID format
    hasError.value = true;
    isLoading.value = false;
    return;
  }

  if (!/^[a-zA-Z0-9_-]{1,128}$/.test(csrfToken)) {
    // Log Error Invalid user token format
    hasError.value = true;
    isLoading.value = false;
    return;
  }

  try {
    const { deleteFunctionUrl } = envVars.firebase;
    // Call the function using fetch
    const response = await fetch(deleteFunctionUrl, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ uid, csrfToken }),
    });

    // Parse the response
    const result = await response.json();

    // Check if successful
    if (response.ok && result.success) {
      isValidated.value = true;
    } else {
      throw new Error(result.error || "An unexpected error occurred");
    }
  } catch {
    // Handle validation errors
    // console.error('Validation error:', error)
    hasError.value = true;
  } finally {
    isLoading.value = false;
  }
});
</script>

<template>
  <div class="uninstall-container">
    <header>
      <h1>Uninstall Intentli</h1>
    </header>

    <main>
      <!-- Loading State -->
      <div v-if="isLoading" class="loading-state">
        <p><AnimatedLoadingDots /></p>
      </div>

      <!-- Error State -->
      <div v-else-if="hasError" class="error-state">
        <p>{{ errorMessage }}</p>
      </div>

      <!-- Success State with Google Form -->
      <div v-else-if="isValidated" class="success-state">
        <div class="success-message">
          <p>Your data has been deleted successfully.</p>
          <p>Please help us improve by sharing why you uninstalled:</p>
        </div>

        <div class="form-options">
          <div class="embedded-form">
            <iframe
              :src="googleFormUrl + '?embedded=true'"
              frameborder="0"
              width="640"
              height="800"
            >
              Deleting Data...
            </iframe>
          </div>
        </div>
      </div>
    </main>

    <footer>
      <p>&copy; Intentli - Data Deletion Request</p>
    </footer>
  </div>
</template>

<style scoped>
.uninstall-container {
  max-width: 100%;
  margin: 0 auto;
  padding: 20px;
  text-align: center;
}

.loading-state,
.error-state,
.success-state {
  margin: 30px auto;
  padding: 20px;
  border-radius: 8px;
  background-color: var(--color-background-soft);
  max-width: 90%;
}

.error-state {
  color: #d32f2f;
}

.success-message {
  margin-bottom: 25px;
}

.form-options {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 20px;
}

.open-form-button {
  display: inline-block;
  padding: 12px 24px;
  background-color: #4caf50;
  color: white;
  border-radius: 4px;
  text-decoration: none;
  font-weight: bold;
  transition: background-color 0.3s;
}

.open-form-button:hover {
  background-color: #45a049;
}

.embedded-form {
  width: 100%;
  max-width: 640px;
  margin: 0 auto;
}

iframe {
  max-width: 100%;
}

footer {
  margin-top: 40px;
  font-size: 14px;
  color: var(--color-text-light-2);
}

@media (max-width: 768px) {
  .embedded-form {
    display: none;
  }

  .open-form-button {
    font-size: 16px;
    padding: 16px 32px;
  }

  .success-message {
    margin-bottom: 40px;
  }
}
</style>
